import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { toString } from 'lodash-es';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ErrorWithPlaceholdersApiInData } from '../utils/useDeviceManagementError.util';
import { convertPlaceholdersIfErrorHasPlaceholders } from '../utils/useDeviceManagementError.util';

export interface UseDeviceManagementErrorReturn {
  readonly getErrorMessage: (error: ErrorWithPlaceholdersApiInData, overrideDefaultMessage?: string) => string;
  readonly checkTranslationExistsByType: (type: string) => boolean;
}

export function useDeviceManagementError(): UseDeviceManagementErrorReturn {
  const { t, i18n } = useTranslation([ALL_LNGS_NAMESPACES.ERROR]);

  const checkTranslationExistsByType = useCallback(
    (type: string) => i18n.exists(`error.${type}`, { ns: [ALL_LNGS_NAMESPACES.ERROR] }),
    [i18n],
  );

  const getErrorMessage = useCallback(
    (error: ErrorWithPlaceholdersApiInData, overrideDefaultMessage?: string): string => {
      const errorType = toString(error.data?.type);
      const doesTranslationExist = errorType && checkTranslationExistsByType(errorType);
      const possiblePlaceholders = convertPlaceholdersIfErrorHasPlaceholders(error.data);
      if (doesTranslationExist) {
        return t([`error.${errorType}`, 'error.default'], { ...possiblePlaceholders });
      } else if (error.status) {
        return t([`error.${error.status}`, 'error.default'], { ...possiblePlaceholders });
      } else {
        return overrideDefaultMessage ?? t('error.default', { ...possiblePlaceholders });
      }
    },
    [checkTranslationExistsByType, t],
  );

  return {
    getErrorMessage,
    checkTranslationExistsByType,
  };
}
